import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  SimpleGrid,
  Card,
  CardBody,
  Badge,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
} from "@chakra-ui/react";
import { FaTrophy, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { LeadCaptureForm } from "../../../components/calculator/LeadCaptureForm";
import { CalculatorType } from "../../../generated/graphql";

interface Loan {
  name: string;
  amount: number;
  rate: number;
  term: number;
  fees: number;
  hasOffset: boolean;
  hasRedraw: boolean;
}

const LoanComparisonCalculator: React.FC = () => {
  const [loans, setLoans] = useState<Loan[]>([
    { name: "Loan A", amount: 500000, rate: 5.5, term: 30, fees: 1000, hasOffset: true, hasRedraw: true },
    { name: "Loan B", amount: 500000, rate: 5.2, term: 30, fees: 2500, hasOffset: false, hasRedraw: true },
    { name: "Loan C", amount: 500000, rate: 5.8, term: 30, fees: 500, hasOffset: true, hasRedraw: false },
  ]);

  const [results, setResults] = useState<any>(null);

  const updateLoan = (index: number, field: keyof Loan, value: any) => {
    const newLoans = [...loans];
    newLoans[index] = { ...newLoans[index], [field]: value };
    setLoans(newLoans);
  };

  const calculate = () => {
    const loanResults = loans.map((loan) => {
      const monthlyRate = loan.rate / 100 / 12;
      const months = loan.term * 12;
      const monthlyPayment =
        (loan.amount * monthlyRate * Math.pow(1 + monthlyRate, months)) /
        (Math.pow(1 + monthlyRate, months) - 1);

      const totalInterest = monthlyPayment * months - loan.amount;
      const totalCost = loan.amount + totalInterest + loan.fees;

      return {
        name: loan.name,
        monthlyPayment: monthlyPayment.toFixed(0),
        totalInterest: totalInterest.toFixed(0),
        totalCost: totalCost.toFixed(0),
        hasOffset: loan.hasOffset,
        hasRedraw: loan.hasRedraw,
      };
    });

    // Find best loan (lowest total cost)
    const bestLoanIndex = loanResults.reduce(
      (minIndex, loan, index, arr) =>
        parseFloat(loan.totalCost) < parseFloat(arr[minIndex].totalCost) ? index : minIndex,
      0
    );

    setResults({ loans: loanResults, bestLoanIndex });
  };

  const reset = () => {
    setLoans([
      { name: "Loan A", amount: 500000, rate: 5.5, term: 30, fees: 1000, hasOffset: true, hasRedraw: true },
      { name: "Loan B", amount: 500000, rate: 5.2, term: 30, fees: 2500, hasOffset: false, hasRedraw: true },
      { name: "Loan C", amount: 500000, rate: 5.8, term: 30, fees: 500, hasOffset: true, hasRedraw: false },
    ]);
    setResults(null);
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <SEOHead
        pagePath="/calculators/loan-comparison"
        fallbackTitle="Loan Comparison Calculator"
        fallbackDescription="Compare multiple home loans side-by-side to find the best deal"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bgGradient="linear(to-r, purple.600, pink.400)" color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center">
            <Icon as={FaTrophy} boxSize={12} />
            <Heading size="2xl" fontWeight="bold">
              Loan Comparison Calculator
            </Heading>
            <Text fontSize="xl" maxW="2xl">
              Compare up to 3 loans side-by-side to find the best deal
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Side-by-side Table Layout */}
      <Container maxW="container.xl" py={12}>
        <VStack spacing={8} align="stretch">
          <Card>
            <CardBody>
              <Table variant="simple" size="md">
                <Thead>
                  <Tr>
                    <Th width="200px">Feature</Th>
                    {loans.map((loan, index) => (
                      <Th key={index} textAlign="center">
                        <Input
                          value={loan.name}
                          onChange={(e) => updateLoan(index, "name", e.target.value)}
                          textAlign="center"
                          fontWeight="bold"
                          fontSize="lg"
                          variant="unstyled"
                        />
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td fontWeight="semibold">Loan Amount</Td>
                    {loans.map((loan, index) => (
                      <Td key={index}>
                        <Input
                          type="number"
                          value={loan.amount}
                          onChange={(e) => updateLoan(index, "amount", parseFloat(e.target.value) || 0)}
                        />
                      </Td>
                    ))}
                  </Tr>

                  <Tr>
                    <Td fontWeight="semibold">Interest Rate (%)</Td>
                    {loans.map((loan, index) => (
                      <Td key={index}>
                        <Input
                          type="number"
                          step="0.01"
                          value={loan.rate}
                          onChange={(e) => updateLoan(index, "rate", parseFloat(e.target.value) || 0)}
                        />
                      </Td>
                    ))}
                  </Tr>

                  <Tr>
                    <Td fontWeight="semibold">Loan Term (years)</Td>
                    {loans.map((loan, index) => (
                      <Td key={index}>
                        <Input
                          type="number"
                          value={loan.term}
                          onChange={(e) => updateLoan(index, "term", parseFloat(e.target.value) || 0)}
                        />
                      </Td>
                    ))}
                  </Tr>

                  <Tr>
                    <Td fontWeight="semibold">Upfront Fees</Td>
                    {loans.map((loan, index) => (
                      <Td key={index}>
                        <Input
                          type="number"
                          value={loan.fees}
                          onChange={(e) => updateLoan(index, "fees", parseFloat(e.target.value) || 0)}
                        />
                      </Td>
                    ))}
                  </Tr>

                  <Tr>
                    <Td fontWeight="semibold">Offset Account</Td>
                    {loans.map((loan, index) => (
                      <Td key={index} textAlign="center">
                        <Checkbox
                          isChecked={loan.hasOffset}
                          onChange={(e) => updateLoan(index, "hasOffset", e.target.checked)}
                        />
                      </Td>
                    ))}
                  </Tr>

                  <Tr>
                    <Td fontWeight="semibold">Redraw Facility</Td>
                    {loans.map((loan, index) => (
                      <Td key={index} textAlign="center">
                        <Checkbox
                          isChecked={loan.hasRedraw}
                          onChange={(e) => updateLoan(index, "hasRedraw", e.target.checked)}
                        />
                      </Td>
                    ))}
                  </Tr>
                </Tbody>
              </Table>
            </CardBody>
          </Card>

          <HStack spacing={4} justify="center">
            <Button colorScheme="purple" size="lg" onClick={calculate} px={12}>
              Compare Loans
            </Button>
            <Button variant="outline" size="lg" onClick={reset}>
              Reset
            </Button>
          </HStack>

          {/* Results Table */}
          {results && (
            <Card bg="purple.50" borderWidth={2} borderColor="purple.400">
              <CardBody>
                <VStack spacing={6} align="stretch">
                  <Heading size="lg" color="purple.700" textAlign="center">
                    Comparison Results
                  </Heading>

                  <Table variant="simple" size="md">
                    <Thead>
                      <Tr>
                        <Th width="200px"></Th>
                        {results.loans.map((loan: any, index: number) => (
                          <Th key={index} textAlign="center">
                            <VStack spacing={1}>
                              <Text>{loan.name}</Text>
                              {index === results.bestLoanIndex && (
                                <Badge colorScheme="green" fontSize="sm">
                                  <HStack spacing={1}>
                                    <Icon as={FaTrophy} />
                                    <Text>Best Deal</Text>
                                  </HStack>
                                </Badge>
                              )}
                            </VStack>
                          </Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Tr>
                        <Td fontWeight="semibold">Monthly Payment</Td>
                        {results.loans.map((loan: any, index: number) => (
                          <Td
                            key={index}
                            textAlign="center"
                            fontWeight="bold"
                            fontSize="lg"
                            color={index === results.bestLoanIndex ? "green.600" : "gray.700"}
                          >
                            ${parseInt(loan.monthlyPayment).toLocaleString()}
                          </Td>
                        ))}
                      </Tr>

                      <Tr>
                        <Td fontWeight="semibold">Total Interest</Td>
                        {results.loans.map((loan: any, index: number) => (
                          <Td
                            key={index}
                            textAlign="center"
                            fontWeight="bold"
                            color={index === results.bestLoanIndex ? "green.600" : "gray.700"}
                          >
                            ${parseInt(loan.totalInterest).toLocaleString()}
                          </Td>
                        ))}
                      </Tr>

                      <Tr bg="purple.100">
                        <Td fontWeight="bold" fontSize="lg">
                          Total Cost
                        </Td>
                        {results.loans.map((loan: any, index: number) => (
                          <Td
                            key={index}
                            textAlign="center"
                            fontWeight="bold"
                            fontSize="xl"
                            color={index === results.bestLoanIndex ? "green.600" : "gray.700"}
                          >
                            ${parseInt(loan.totalCost).toLocaleString()}
                          </Td>
                        ))}
                      </Tr>

                      <Tr>
                        <Td fontWeight="semibold">Offset Account</Td>
                        {results.loans.map((loan: any, index: number) => (
                          <Td key={index} textAlign="center">
                            <Icon
                              as={loan.hasOffset ? FaCheckCircle : FaTimesCircle}
                              color={loan.hasOffset ? "green.500" : "red.500"}
                              boxSize={6}
                            />
                          </Td>
                        ))}
                      </Tr>

                      <Tr>
                        <Td fontWeight="semibold">Redraw Facility</Td>
                        {results.loans.map((loan: any, index: number) => (
                          <Td key={index} textAlign="center">
                            <Icon
                              as={loan.hasRedraw ? FaCheckCircle : FaTimesCircle}
                              color={loan.hasRedraw ? "green.500" : "red.500"}
                              boxSize={6}
                            />
                          </Td>
                        ))}
                      </Tr>
                    </Tbody>
                  </Table>

                  <LeadCaptureForm
                    calculatorType={CalculatorType.LoanComparison}
                    calculatorName="Loan Comparison Calculator"
                    calculatorPath="/calculators/loan-comparison"
                    inputData={{ loans }}
                    resultData={results}
                    hasResults={!!results}
                    accentColor="purple"
                  />
                </VStack>
              </CardBody>
            </Card>
          )}
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default LoanComparisonCalculator;
